/**
 * Redirects to page 1 when the current URL has a page param but the API returns no data.
 * Common fix for: user on page 2, deletes the last item on that page → list goes empty;
 * we redirect to page 1 so the list shows the previous page's data.
 *
 * Use on any list screen that syncs page with URL (e.g. ?page=2) and uses paginated API.
 */

import { useEffect } from 'react';
import type { SetURLSearchParams } from 'react-router-dom';

export interface UseRedirectWhenPageEmptyOptions {
  /** Current URL search params (e.g. from useSearchParams()[0]) */
  searchParams: URLSearchParams;
  /** Setter for URL search params (e.g. from useSearchParams()[1]) */
  setSearchParams: SetURLSearchParams;
  /** Whether the list query is loading */
  isLoading: boolean;
  /** Whether the list query failed (do not redirect on error; let error UI show) */
  isError?: boolean;
  /** Number of items in the current list response */
  dataLength: number;
  /** URL param key for page number (default 'page') */
  pageParamKey?: string;
}

/**
 * When the list API returns empty data but the URL has page > 1, redirect to page 1
 * so the user sees the list again (e.g. after deleting the last item on the current page).
 */
export function useRedirectWhenPageEmpty({
  searchParams,
  setSearchParams,
  isLoading,
  isError = false,
  dataLength,
  pageParamKey = 'page',
}: UseRedirectWhenPageEmptyOptions): void {
  useEffect(() => {
    if (isLoading || isError || dataLength > 0) return;

    const raw = searchParams.get(pageParamKey);
    const page = Number(raw) || 1;
    if (page <= 1) return;

    const next = new URLSearchParams(searchParams);
    next.set(pageParamKey, '1');
    setSearchParams(next);
  }, [searchParams, setSearchParams, isLoading, isError, dataLength, pageParamKey]);
}
